import { SORT_DIRECTIONS } from 'constants/sortDirectionsEnum';
import { MetricBreakdownCard } from 'modules/campaignAnalytics/components/MetricBreakdownCard';
import { SkeletonWrapper } from 'modules/campaignAnalytics/components/Styled';
import { ViewState } from 'modules/campaignAnalytics/types';
import { useMemo, useState } from 'react';
import { FlexSpacer } from 'ui/flexSpacer';
import { Skeleton } from 'ui/skeleton';
import { Table } from 'ui/table/Table';
import { EmptyCard } from '../EmptyCard';
import { COLUMNS } from './constants';
import { useCampaignCustomReplies } from './hooks';
import { CustomRepliesTableWrapper, StyledTableHeader, StyledTableRows } from './Styled';
import { CampaignCustomReplyDefinition, ColumnValue } from './type';

const tooltipText =
  "Custom replies reflect most recent 250 replies received within the first 24 hours after a campaign is sent, or if a recipient long-presses a message and selects 'Reply'.";

type CardContentProps = {
  viewState: ViewState;
  rows: CampaignCustomReplyDefinition[];
  sortedBy: keyof CampaignCustomReplyDefinition;
  setSortedBy: (sortBy: keyof CampaignCustomReplyDefinition) => void;
  sortDirection: string | SORT_DIRECTIONS;
  setSortDirection: (sortDirection: string | SORT_DIRECTIONS) => void;
};

const CardContent = ({
  viewState,
  rows,
  sortedBy,
  setSortDirection,
  sortDirection,
  setSortedBy,
}: CardContentProps) => {
  switch (viewState) {
    case ViewState.Loading:
      return (
        <SkeletonWrapper height={450} padding={24}>
          <Skeleton />
        </SkeletonWrapper>
      );

    case ViewState.NoData:
      return (
        <FlexSpacer width="100%" justifyContent="center">
          <EmptyCard type="custom" />
        </FlexSpacer>
      );

    case ViewState.ContentReady:
      return (
        <CustomRepliesTableWrapper>
          <Table>
            <StyledTableHeader
              columns={COLUMNS}
              setSortDirection={setSortDirection}
              setSortedBy={(sortBy) => setSortedBy(sortBy as ColumnValue)}
              sortDirection={sortDirection}
              sortedBy={sortedBy}
              isSticky
            />
            <StyledTableRows columns={COLUMNS} rows={rows} />
          </Table>
        </CustomRepliesTableWrapper>
      );

    default:
      return null;
  }
};

export const CustomRepliesCard = () => {
  const {
    data: { campaignCustomReplies } = {},
    isLoading,
  } = useCampaignCustomReplies();
  const [sortedBy, setSortedBy] = useState<ColumnValue>('createdAt');
  const [sortDirection, setSortDirection] = useState<string | SORT_DIRECTIONS>(
    SORT_DIRECTIONS.DESCENDING,
  );

  const rows = useMemo(() => {
    if (!campaignCustomReplies || campaignCustomReplies.length === 0) {
      return [];
    }

    const campaignCustomRepliesWithId = campaignCustomReplies.map((row) => ({
      ...row,
      id: `${row.createdAt}_${row.userEmail}`,
    }));

    return campaignCustomRepliesWithId.sort((a, b) => {
      const aValue = a[sortedBy];
      const bValue = b[sortedBy];

      if (aValue === bValue) return 0;

      const isAscending = sortDirection === SORT_DIRECTIONS.ASCENDING;

      if (aValue == null) return isAscending ? -1 : 1;
      if (bValue == null) return isAscending ? 1 : -1;

      if (sortedBy === 'createdAt') {
        const aTime = new Date(aValue as string).getTime();
        const bTime = new Date(bValue as string).getTime();
        return isAscending ? aTime - bTime : bTime - aTime;
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return isAscending ? aValue - bValue : bValue - aValue;
      }

      return isAscending
        ? String(aValue).localeCompare(String(bValue))
        : String(bValue).localeCompare(String(aValue));
    });
  }, [campaignCustomReplies, sortedBy, sortDirection]);

  let viewState: ViewState;

  if (isLoading) {
    viewState = ViewState.Loading;
  } else if (!campaignCustomReplies || campaignCustomReplies.length === 0) {
    viewState = ViewState.NoData;
  } else {
    viewState = ViewState.ContentReady;
  }

  return (
    <MetricBreakdownCard
      title="Custom replies"
      titleHelperContent={tooltipText}
      data-test="custom-replies-card"
    >
      <CardContent
        viewState={viewState}
        rows={rows}
        sortedBy={sortedBy}
        sortDirection={sortDirection}
        setSortedBy={setSortedBy}
        setSortDirection={setSortDirection}
      />
    </MetricBreakdownCard>
  );
};
